import { createUseStyles } from 'react-jss'
import { THEME } from 'components/theme'

// iOS/Android TransactionalAlertSettingsScreen: two white cards (Language, Transaction
// Alert) on the grey page ground, the alert card ending in a phone-frame SMS preview.
export const TransactionAlertSettingsStyles = createUseStyles({
  root: {
    backgroundColor: THEME.brandGrey,
    padding: '6px 12px 12px',
    boxSizing: 'border-box'
  },
  // Registered end-user strip: edge-to-edge under the header, orange tint, bell + copy
  registeredStrip: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: '-6px -12px 6px',
    padding: '10px 16px',
    backgroundColor: THEME.brandTintOrange,
    color: THEME.brandDarkerGrey,
    fontSize: 13,
    lineHeight: '18px',
    '& svg': {
      flexShrink: 0,
      fill: THEME.brandDarkOrange
    }
  },
  card: {
    backgroundColor: THEME.brandWhite,
    borderRadius: 8,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.12)',
    margin: '6px 0',
    overflow: 'hidden'
  },
  cardBody: {
    padding: 12
  },
  clickable: {
    cursor: 'pointer'
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12
  },
  titleCol: {
    flex: 1,
    minWidth: 0
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  // Leading icon in primary green (iOS tints ic_language / ic_icon_sms_outline with primary)
  // Tint is set inline on the <svg> (see ICON_TINT) so it beats the dark-mode ink rule that
  // rewrites baked #212121 fills; the padding rect keeps its own fill="none".
  leadIcon: {
    width: 24,
    height: 24,
    flexShrink: 0
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: THEME.brandBlack,
    lineHeight: '24px'
  },
  subtitle: {
    marginTop: 4,
    fontSize: 14,
    lineHeight: '20px',
    color: THEME.brandDarkerGrey
  },
  // Right side of the Language card: current language in green + a down chevron
  valueRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
    height: 24
  },
  value: {
    fontSize: 14,
    fontWeight: 600,
    color: THEME.brandGreen
  },
  chevron: {
    width: 24,
    height: 24,
    transition: 'transform 160ms ease'
  },
  chevronOpen: {
    transform: 'rotate(180deg)'
  },
  // Language picker (Android/iOS use a sheet; on desktop the card itself expands)
  langList: {
    borderTop: `1px solid ${THEME.brandLightGrey}`,
    maxHeight: 264,
    overflowY: 'auto'
  },
  langItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    fontSize: 14,
    color: THEME.brandBlack,
    cursor: 'pointer',
    borderBottom: `1px solid ${THEME.brandLightGrey}`,
    '&:last-child': {
      borderBottom: 'none'
    },
    '&:hover': {
      backgroundColor: THEME.brandGrey
    }
  },
  langItemSelected: {
    color: THEME.brandGreen,
    fontWeight: 600
  },
  langTick: {
    width: 16,
    height: 16
  },
  // Transaction Alert card: OFF/ON word + switch
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0
  },
  statusText: {
    fontSize: 16,
    fontWeight: 600,
    color: THEME.brandDarkerGrey
  },
  statusOn: {
    color: THEME.brandGreen
  },
  description: {
    marginTop: 8,
    fontSize: 14,
    lineHeight: '20px',
    color: THEME.brandDarkerGrey
  },
  // Phone-frame preview docked to the card's bottom edge: rounded top, square bottom
  // clipped flush by the card. Pinned light (artwork rule), like the reminder mock-ups.
  phone: {
    width: '70%',
    margin: '12px auto 0',
    borderRadius: '24px 24px 0 0',
    padding: '6px 6px 0',
    backgroundColor: '#212121',
    boxSizing: 'border-box',
    transition: 'opacity 160ms ease'
  },
  phoneOff: {
    backgroundColor: '#9e9e9e'
  },
  phoneScreen: {
    position: 'relative',
    borderRadius: '18px 18px 0 0',
    backgroundColor: '#fff',
    padding: '25px 14px 10px'
  },
  phoneNotch: {
    position: 'absolute',
    top: 8,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#212121'
  },
  phoneNotchOff: {
    backgroundColor: '#9e9e9e'
  },
  bubble: {
    borderRadius: 8,
    padding: 12,
    backgroundColor: '#e2f2ff',
    color: '#212121',
    fontSize: 12,
    lineHeight: '16px',
    whiteSpace: 'pre-line',
    wordBreak: 'break-word'
  },
  bubbleOff: {
    backgroundColor: '#f2f8f8',
    color: '#9e9e9e'
  },
  // Upgrade-to-Premium sheet (opened when a non-premium merchant flips the alert on)
  sheetScrim: {
    position: 'fixed',
    inset: 0,
    zIndex: 100002,
    backgroundColor: 'rgba(0, 0, 0, 0.32)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    boxSizing: 'border-box'
  },
  sheet: {
    backgroundColor: THEME.brandWhite,
    borderRadius: 16,
    width: 400,
    maxWidth: 'calc(100vw - 48px)',
    padding: '28px 24px 24px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  },
  upgradeBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  },
  sheetText: {
    fontSize: 15,
    lineHeight: '22px',
    color: THEME.brandBlack,
    textAlign: 'center'
  },
  sheetButtons: {
    display: 'flex',
    gap: 12
  },
  // iOS TwinBottomButtons: outlined primary-green Cancel, filled Buy Plan with its badge icon
  sheetSecondary: {
    flex: 1,
    height: 44,
    borderRadius: 22,
    border: `1px solid ${THEME.brandGreen}`,
    background: 'transparent',
    color: THEME.brandGreen,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer'
  },
  // SMS-or-WhatsApp channel sheet (My Plan's variant rows, no prices)
  channelSheetBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  channelHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  channelTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: 700,
    color: THEME.brandBlack
  },
  channelClose: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 22,
    lineHeight: 1,
    color: THEME.brandBlack,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: THEME.brandGrey
    }
  },
  channelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    borderRadius: 16,
    backgroundColor: THEME.screenCard,
    border: '2px solid transparent',
    cursor: 'pointer'
  },
  channelRowSelected: {
    borderColor: THEME.brandGreen
  },
  channelDisc: {
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: THEME.iconDiscMuted,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: THEME.brandBlack,
    '& svg': {
      width: 20,
      height: 20
    }
  },
  channelLabel: {
    flex: 1,
    fontSize: 16,
    fontWeight: 700,
    color: THEME.brandBlack
  },
  channelMark: {
    flexShrink: 0,
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  channelRing: {
    width: 20,
    height: 20,
    boxSizing: 'border-box',
    borderRadius: '50%',
    border: `2px solid ${THEME.iconDiscMuted}`
  },
  channelProceed: {
    width: '100%',
    height: 48,
    borderRadius: 24,
    border: 'none',
    background: THEME.brandGreen,
    color: THEME.onBrand,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default'
    }
  },
  channelCheck: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: THEME.brandGreen,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  sheetPrimary: {
    flex: 1,
    height: 44,
    borderRadius: 22,
    border: 'none',
    background: THEME.brandGreen,
    color: THEME.onBrand,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default'
    }
  },
  // Sheet confined to the drawer / dialog card it was opened from (iOS bottom sheet)
  '@keyframes sheetSlideUp': {
    from: { transform: 'translateY(24px)', opacity: 0 },
    to: { transform: 'translateY(0)', opacity: 1 }
  },
  anchoredScrim: {
    inset: 'auto',
    alignItems: 'flex-end',
    padding: 0
  },
  bottomSheet: {
    width: '100%',
    maxWidth: 'none',
    borderRadius: '16px 16px 0 0',
    padding: '16px 20px 24px',
    animationName: '$sheetSlideUp',
    animationDuration: '220ms',
    animationTimingFunction: 'ease-out'
  },
  bottomSheetRounded: {
    borderRadius: 16
  },
  // Over a rounded dialog card the scrim must take the card's radius too, or its square corners
  // show past the card's rounded ones as dark notches
  anchoredScrimRounded: {
    borderRadius: 16,
    overflow: 'hidden'
  }
})
